import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type {
    EDashboardWidgetTypes,
    IDashboardWidgetProps,
    TWidgetConfig,
    WidgetParamsMap,
} from '@sharedInterfaces/IDashboard';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { type Layout, Responsive, WidthProvider } from 'react-grid-layout';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { cloneDeep } from '@apollo/client/utilities';
import { agentChatDashboardWidgetDefinition } from '@src/App/Components/DashboardWidgets/AgentChatDashboardWidget/AgentChatDashboardWidget';
import { buttonBoxDashboardWidgetDefinition } from '@src/App/Components/DashboardWidgets/ButtonBoxDashboardWidget/ButtonBoxDashboardWidget';
import { notificationDashboardWidgetDefinition } from '@src/App/Components/DashboardWidgets/NotificationDashboardWidget/NotificationDashboardWidget';
import { onboardingDashboardWidgetDefinition } from '@src/App/Components/DashboardWidgets/OnboardingDashboardWidget/OnboardingDashboardWidget';
import { personListDashBoardWidgetDefinition } from '@src/App/Components/DashboardWidgets/PersonListDashboardWidget/PersonListDashboardWidget';
import { richTextDashBoardWidgetDefinition } from '@src/App/Components/DashboardWidgets/RichTextDashboardWidget/RichTextDashboardWidget';
import { RootDashboardWidget } from '@src/App/Components/DashboardWidgets/RootDashboardWidget/RootDashboardWidget';

import styles from './Dashboard.module.css';
import EditButton from './EditButton';

const ResponsiveGridLayout = WidthProvider(Responsive);
export interface DashboardState {
    layout: Layout[];
    widgets: TWidgetConfig[];
}

export type TWidgetDefinition = {
    [K in EDashboardWidgetTypes]: {
        type: K;
        render: React.FC<IDashboardWidgetProps<K>>;
        overrideCardDesign: boolean;
        defaultParams: WidgetParamsMap[K];
    };
}[EDashboardWidgetTypes];

const WIDGET_DEFINITIONS = {
    richTextWidget: richTextDashBoardWidgetDefinition,
    personListWidget: personListDashBoardWidgetDefinition,
    buttonBoxWidget: buttonBoxDashboardWidgetDefinition,
    agentChatWidget: agentChatDashboardWidgetDefinition,
    onboardingWidget: onboardingDashboardWidgetDefinition,
    notificationWidget: notificationDashboardWidgetDefinition,
} as const satisfies Record<EDashboardWidgetTypes, TWidgetDefinition>;

type WidgetDefMap = typeof WIDGET_DEFINITIONS;

function getDefaultParams<T extends EDashboardWidgetTypes>(type: T): WidgetDefMap[T]['defaultParams'] {
    return WIDGET_DEFINITIONS[type].defaultParams;
}

interface DashboardProps {
    dashboardId: string;
    isEditing: boolean;
    showEditButton?: true;
    onEditToggle: (hasChanges: DashboardState) => Promise<unknown>;
}
export default function Dashboard({ dashboardId, isEditing, showEditButton, onEditToggle }: DashboardProps) {
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).dashboard;
    const employee = useSelector((state: AppState) => state.employee);
    const originalDashboardState = useSelector((state: AppState) => state.dashboard.find((d) => d.id === dashboardId));
    const [dashboardState, setDashboardState] = useState<DashboardState>({ layout: [], widgets: [] });
    const [breakpoint, setBreakpoint] = useState('lg');
    const [newType, setNewType] = useState<EDashboardWidgetTypes | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Use external isEditing state if provided, otherwise use internal state

    useEffect(() => {
        if (originalDashboardState) {
            const parsed: DashboardState = cloneDeep(originalDashboardState);
            parsed.layout = parsed.layout.map((l) => ({ i: l.i, x: l.x || 0, y: l.y || 0, w: l.w || 3, h: l.h || 2 }));

            setDashboardState(parsed);
        }
    }, [originalDashboardState]);

    const onLayoutChange = (layout: Layout[]) => {
        // console.log('Layout changed:', layout, 'Breakpoint:', breakpoint);
        // if (breakpoint === 'lg') {
        setDashboardState((s) => ({ ...s, layout }));
        // }
    };
    const onBreakpointChange = (bp: string) => setBreakpoint(bp);

    const handleAdd = () => {
        if (!newType) return;

        const id = Date.now().toString();
        const maxY = dashboardState.layout.reduce((max, l) => Math.max(max, l.y + l.h), 0);
        const newWidget: TWidgetConfig<EDashboardWidgetTypes> = {
            i: id,
            type: newType as EDashboardWidgetTypes,
            title: langStrings.newWidget,
            params: getDefaultParams(newType as EDashboardWidgetTypes),
        };
        console.log(newWidget);
        setDashboardState((s) => {
            const updatedWidgets: TWidgetConfig[] = [...s.widgets, newWidget];
            return {
                widgets: updatedWidgets,
                layout: [...s.layout, { i: id, x: 0, y: maxY, w: 3, h: 2 }],
            };
        });
    };
    const handleDelete = (id: string) =>
        setDashboardState((s) => ({
            widgets: s.widgets.filter((w) => w.i !== id),
            layout: s.layout.filter((l) => l.i !== id),
        }));
    const handleTitleChange = (id: string, title: string) =>
        setDashboardState((s) => ({ ...s, widgets: s.widgets.map((w) => (w.i === id ? { ...w, title } : w)) }));
    const handleParamChange = (id: string, key: string, v: unknown) =>
        setDashboardState((s) => {
            const updatedWidgets = s.widgets.map((w) => {
                if (w.i !== id) return w;
                return {
                    ...w,
                    params: {
                        ...(w.params ?? getDefaultParams(w.type)),
                        [key]: v,
                    },
                };
            });
            return { ...s, widgets: updatedWidgets };
        });
    const toggleEdit = () => {
        console.log('toogle edit');
        console.log('Original State DB:', originalDashboardState);
        if (!originalDashboardState) return;

        setIsSaving(true);
        onEditToggle(dashboardState).finally(() => {
            setIsSaving(false);
        });
    };

    return (
        <div className={styles.dashboard} style={{ position: 'relative' }}>
            {/* <pre>
                {JSON.stringify(originalDashboardState?.layout, null, 4)}
            </pre> */}
            {/* EditButton is now in the ContentHeader, so we only show it here if we're using internal state */}
            {showEditButton && <EditButton isEditing={isEditing} onClick={toggleEdit} disabled={isSaving} />}
            {isEditing && (
                <>
                    <TextField
                        select
                        value={newType}
                        onChange={(e) => setNewType(e.target.value as EDashboardWidgetTypes)}
                        size="medium"
                        style={{ width: 200, marginLeft: 8 }}
                        label={langStrings.selectWidget}
                    >
                        {Object.values(WIDGET_DEFINITIONS).map((def) => (
                            <MenuItem key={def.type} value={def.type}>
                                {langStrings.widgetLabels[def.type]}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        sx={{ ml: 1 }}
                        disabled={!newType}
                    >
                        {langStrings.addWidget}
                    </Button>
                </>
            )}
            <ResponsiveGridLayout
                compactType={null}
                preventCollision={true}
                verticalCompact={false}
                className={styles.dashboardLayout}
                layouts={{ lg: dashboardState.layout }}
                breakpoints={{ lg: 1200, md: 960, sm: 600, xs: 0 }}
                cols={{ lg: 6, md: 4, sm: 2, xs: 1 }}
                rowHeight={100}
                onLayoutChange={onLayoutChange}
                onBreakpointChange={onBreakpointChange}
                isDraggable={isEditing}
                isResizable={isEditing}
                resizeHandles={['se']}
                draggableHandle=".drag-handle"
                useCSSTransforms={false}
            >
                {dashboardState.widgets.map((widget) => {
                    const layoutItem = dashboardState.layout.find((l) => l.i === widget.i) || {
                        i: widget.i,
                        x: 0,
                        y: 0,
                        w: 3,
                        h: 2,
                    };
                    const def = WIDGET_DEFINITIONS[widget.type];
                    if (!def) return null;
                    return (
                        <div
                            key={widget.i}
                            data-grid={layoutItem}
                            style={{
                                height: '100%',
                                width: '100%',
                                overflow: 'auto',
                                clipPath: 'inset(0)',
                                contain: 'layout style paint',
                            }}
                        >
                            <RootDashboardWidget
                                id={widget.i}
                                title={widget.title}
                                isEditing={isEditing}
                                overrideCardDesign={def.overrideCardDesign || false}
                                onDelete={handleDelete}
                                onTitleChange={handleTitleChange}
                            >
                                {React.createElement(
                                    def.render as React.FC<{
                                        id: string;
                                        params: unknown;
                                        isEditing: boolean;
                                        onParamChange: unknown;
                                    }>,
                                    {
                                        id: widget.i,
                                        params: widget.params ?? {},
                                        isEditing,
                                        onParamChange: handleParamChange,
                                    },
                                )}
                            </RootDashboardWidget>
                        </div>
                    );
                })}
            </ResponsiveGridLayout>
        </div>
    );
}
